<template>
  <div class="range-selector-container">
    <canvas ref="rangeCanvas"></canvas>
  </div>
</template>

<script setup lang="ts">
import Chart from "@/chart-setup";
import { type ChartConfiguration, type ChartDataset } from "chart.js";
import { onMounted, onUnmounted, useTemplateRef, watch } from "vue";

interface RangeSelectorProps {
  datasets: ChartDataset<"line", number[]>[];
  labels: string[];
  onRangeChange: (startIndex: number, endIndex: number) => void;
}

type DragMode = "none" | "create" | "resize-left" | "resize-right" | "move";

const props = defineProps<RangeSelectorProps>();

const rangeCanvas = useTemplateRef<HTMLCanvasElement>("rangeCanvas");
let chartInstance: Chart<"line", number[], string> | null = null;
let isDragging = false;
let dragMode: DragMode = "none";
let dragStartIndex = 0;
let initialSelectionStart = 0; // Store initial selection when drag starts
let initialSelectionEnd = 0; // Store initial selection when drag starts
let selectionStart = 0;
let selectionEnd = 0;
let listenersAttached = false;

// Calculate default range (last 1 year)
const getDefaultRange = () => {
  const totalPoints = props.labels.length;
  if (totalPoints === 0) return { start: 0, end: 0 };

  const end = totalPoints - 1;

  // Try to parse dates from labels to find actual date one year ago
  if (props.labels.length > 0) {
    const lastLabel = props.labels[end];
    const lastDate = new Date(lastLabel);

    // If we can parse the last date, calculate one year ago
    if (!isNaN(lastDate.getTime())) {
      const oneYearAgo = new Date(lastDate);
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

      // Find the index closest to one year ago
      let start = 0;
      for (let i = 0; i < props.labels.length; i++) {
        const currentDate = new Date(props.labels[i]);
        if (!isNaN(currentDate.getTime()) && currentDate >= oneYearAgo) {
          start = i;
          break;
        }
      }

      return { start, end };
    }
  }

  // Fallback: use trading days assumption if date parsing fails
  const oneYearPoints = Math.min(230, totalPoints);
  const start = Math.max(0, end - oneYearPoints + 1);

  return { start, end };
};

const addRangeCanvasListeners = (): void => {
  if (!rangeCanvas.value || listenersAttached) return;

  // Add mouse event listeners for dragging
  rangeCanvas.value.addEventListener("mousedown", handleMouseDown);
  rangeCanvas.value.addEventListener("mousemove", handleMouseMove);
  rangeCanvas.value.addEventListener("mouseup", handleMouseUp);
  rangeCanvas.value.addEventListener("mouseleave", handleMouseUp);

  // Add touch event listeners for mobile with passive: false for touchstart/touchmove
  rangeCanvas.value.addEventListener("touchstart", handleTouchStart, {
    passive: false,
  });
  rangeCanvas.value.addEventListener("touchmove", handleTouchMove, {
    passive: false,
  });
  rangeCanvas.value.addEventListener("touchend", handleTouchEnd);

  listenersAttached = true;
};

const removeRangeCanvasListeners = (): void => {
  if (!rangeCanvas.value || !listenersAttached) return;

  // Remove mouse event listeners
  rangeCanvas.value.removeEventListener("mousedown", handleMouseDown);
  rangeCanvas.value.removeEventListener("mousemove", handleMouseMove);
  rangeCanvas.value.removeEventListener("mouseup", handleMouseUp);
  rangeCanvas.value.removeEventListener("mouseleave", handleMouseUp);

  // Remove touch event listeners
  rangeCanvas.value.removeEventListener("touchstart", handleTouchStart);
  rangeCanvas.value.removeEventListener("touchmove", handleTouchMove);
  rangeCanvas.value.removeEventListener("touchend", handleTouchEnd);

  listenersAttached = false;
};

const resetRangeCanvasListeners = (): void => {
  removeRangeCanvasListeners();
  addRangeCanvasListeners();
};

const createRangeChart = () => {
  if (!rangeCanvas.value) return;

  // Destroy existing chart
  if (chartInstance) {
    chartInstance.destroy();
  }

  const ctx = rangeCanvas.value.getContext("2d");
  if (!ctx) return;

  // Create simplified datasets for the mini view
  const miniDatasets = props.datasets.map((dataset) => ({
    ...dataset,
    borderWidth: 1,
    pointRadius: 0,
    pointHoverRadius: 0,
    tension: 0.1,
  }));

  const config: ChartConfiguration<"line", number[], string> = {
    type: "line",
    data: {
      labels: props.labels,
      datasets: miniDatasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: "index",
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: false,
            },
            pinch: {
              enabled: false,
            },
            mode: "x",
          },
          pan: {
            enabled: false,
          },
        },
      },
      scales: {
        x: {
          display: true,
          grid: {
            display: false,
          },
          ticks: {
            maxTicksLimit: 10,
            font: {
              size: 10,
            },
          },
        },
        y: {
          display: false,
          grid: {
            display: false,
          },
        },
      },
      animation: false,
    },
    plugins: [
      {
        id: "rangeSelector",
        afterDraw: (chart) => {
          const ctx = chart.ctx;
          const chartArea = chart.chartArea;
          const xScale = chart.scales.x;

          // Draw selection overlay - always draw if we have a selection
          const actualStart = Math.min(selectionStart, selectionEnd);
          const actualEnd = Math.max(selectionStart, selectionEnd);

          if (
            actualStart >= 0 &&
            actualEnd >= 0 &&
            actualEnd < props.labels.length &&
            actualStart <= actualEnd
          ) {
            const startX = xScale.getPixelForValue(actualStart);
            const endX = xScale.getPixelForValue(actualEnd);

            // Draw semi-transparent overlay for non-selected areas
            ctx.save();
            ctx.fillStyle = "rgba(200, 200, 200, 0.3)";

            // Left non-selected area
            if (actualStart > 0) {
              ctx.fillRect(
                chartArea.left,
                chartArea.top,
                startX - chartArea.left,
                chartArea.bottom - chartArea.top
              );
            }

            // Right non-selected area
            if (actualEnd < props.labels.length - 1) {
              ctx.fillRect(
                endX,
                chartArea.top,
                chartArea.right - endX,
                chartArea.bottom - chartArea.top
              );
            }

            // Draw selection border
            ctx.strokeStyle = "#007bff";
            ctx.lineWidth = 2;
            ctx.strokeRect(
              startX,
              chartArea.top,
              endX - startX,
              chartArea.bottom - chartArea.top
            );

            // Draw resize handles - make them more prominent
            const handleWidth = 8;
            const handleHeight = chartArea.bottom - chartArea.top;

            // Left handle
            ctx.fillStyle = "#007bff";
            ctx.fillRect(
              startX - handleWidth / 2,
              chartArea.top,
              handleWidth,
              handleHeight
            );

            // Left handle grip lines
            ctx.strokeStyle = "#ffffff";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(startX - 2, chartArea.top + 10);
            ctx.lineTo(startX - 2, chartArea.bottom - 10);
            ctx.moveTo(startX + 2, chartArea.top + 10);
            ctx.lineTo(startX + 2, chartArea.bottom - 10);
            ctx.stroke();

            // Right handle
            ctx.fillStyle = "#007bff";
            ctx.fillRect(
              endX - handleWidth / 2,
              chartArea.top,
              handleWidth,
              handleHeight
            );

            // Right handle grip lines
            ctx.strokeStyle = "#ffffff";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(endX - 2, chartArea.top + 10);
            ctx.lineTo(endX - 2, chartArea.bottom - 10);
            ctx.moveTo(endX + 2, chartArea.top + 10);
            ctx.lineTo(endX + 2, chartArea.bottom - 10);
            ctx.stroke();

            ctx.restore();
          }
        },
      },
    ],
  };

  chartInstance = new Chart(ctx, config);

  // Set default selection
  const defaultRange = getDefaultRange();
  selectionStart = defaultRange.start;
  selectionEnd = defaultRange.end;

  // Force initial draw of selection after chart is ready
  requestAnimationFrame(() => {
    setTimeout(() => {
      if (chartInstance) {
        // Force a redraw to show the initial selection
        chartInstance.update("none");
        chartInstance.render();
      }
      props.onRangeChange(selectionStart, selectionEnd);
    }, 100);
  });

  // Reset event listeners to ensure clean state
  resetRangeCanvasListeners();
};

const getDataIndexFromEvent = (event: MouseEvent | Touch): number => {
  if (!chartInstance || !rangeCanvas.value) return -1;

  const rect = rangeCanvas.value.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const xScale = chartInstance.scales.x;

  // Find the closest data point
  let closestIndex = 0;
  let minDistance = Infinity;

  for (let i = 0; i < props.labels.length; i++) {
    const pixelX = xScale.getPixelForValue(i);
    const distance = Math.abs(pixelX - x);
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = i;
    }
  }

  return closestIndex;
};

// Detect what part of the selection is being interacted with
const getInteractionMode = (event: MouseEvent | Touch): DragMode => {
  if (!chartInstance || !rangeCanvas.value) return "none";

  const rect = rangeCanvas.value.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const xScale = chartInstance.scales.x;

  const actualStart = Math.min(selectionStart, selectionEnd);
  const actualEnd = Math.max(selectionStart, selectionEnd);

  const startX = xScale.getPixelForValue(actualStart);
  const endX = xScale.getPixelForValue(actualEnd);

  const handleSize = 8; // Size of the resize handles
  const tolerance = 5; // Extra pixels for easier grabbing

  // Check if clicking on left handle
  if (Math.abs(x - startX) <= handleSize + tolerance) {
    return "resize-left";
  }

  // Check if clicking on right handle
  if (Math.abs(x - endX) <= handleSize + tolerance) {
    return "resize-right";
  }

  // Check if clicking within the selection area (for moving)
  if (x >= startX && x <= endX) {
    return "move";
  }

  // Otherwise, create new selection
  return "create";
};

const handleMouseDown = (event: MouseEvent) => {
  isDragging = true;
  dragMode = getInteractionMode(event);
  dragStartIndex = getDataIndexFromEvent(event);

  // Store initial selection state for move and resize operations
  initialSelectionStart = selectionStart;
  initialSelectionEnd = selectionEnd;

  if (dragMode === "create") {
    // Start new selection
    selectionStart = dragStartIndex;
    selectionEnd = dragStartIndex;
  }
  // For other modes, keep existing selection and modify during drag
};

const handleMouseMove = (event: MouseEvent) => {
  if (!isDragging) {
    // Change cursor based on what would be interacted with
    if (rangeCanvas.value) {
      const mode = getInteractionMode(event);
      switch (mode) {
        case "resize-left":
        case "resize-right":
          rangeCanvas.value.style.cursor = "ew-resize";
          break;
        case "move":
          rangeCanvas.value.style.cursor = "move";
          break;
        default:
          rangeCanvas.value.style.cursor = "crosshair";
      }
    }
    return;
  }

  const currentIndex = getDataIndexFromEvent(event);
  if (currentIndex >= 0) {
    switch (dragMode) {
      case "create": {
        // Creating new selection
        selectionEnd = currentIndex;
        break;
      }

      case "resize-left": {
        // Resizing left edge - use initial end
        selectionStart = currentIndex;
        selectionEnd = initialSelectionEnd;
        break;
      }

      case "resize-right": {
        // Resizing right edge - use initial start
        selectionStart = initialSelectionStart;
        selectionEnd = currentIndex;
        break;
      }

      case "move": {
        // Moving entire selection - use initial boundaries
        const dragDistance = currentIndex - dragStartIndex;
        const initialStart = Math.min(
          initialSelectionStart,
          initialSelectionEnd
        );
        const initialEnd = Math.max(initialSelectionStart, initialSelectionEnd);

        const newStart = initialStart + dragDistance;
        const newEnd = initialEnd + dragDistance;

        // Constrain to bounds
        if (newStart >= 0 && newEnd < props.labels.length) {
          selectionStart = newStart;
          selectionEnd = newEnd;
        } else if (newStart < 0) {
          // Hit left boundary
          selectionStart = 0;
          selectionEnd = initialEnd - initialStart;
        } else if (newEnd >= props.labels.length) {
          // Hit right boundary
          selectionEnd = props.labels.length - 1;
          selectionStart = selectionEnd - (initialEnd - initialStart);
        }
        break;
      }
    }

    // Ensure start is before end (except for move mode)
    if (dragMode !== "move" && selectionStart > selectionEnd) {
      [selectionStart, selectionEnd] = [selectionEnd, selectionStart];
    }

    // Redraw chart
    if (chartInstance) {
      chartInstance.update("none");
    }
  }
};

const handleMouseUp = () => {
  if (isDragging) {
    isDragging = false;
    dragMode = "none";

    // Ensure proper order
    const actualStart = Math.min(selectionStart, selectionEnd);
    const actualEnd = Math.max(selectionStart, selectionEnd);

    props.onRangeChange(actualStart, actualEnd);
  }
};

// Touch event handlers
const handleTouchStart = (event: TouchEvent) => {
  if (event.touches.length === 1) {
    const touch = event.touches[0];
    isDragging = true;
    dragMode = getInteractionMode(touch);
    dragStartIndex = getDataIndexFromEvent(touch);

    // Store initial selection state for move and resize operations
    initialSelectionStart = selectionStart;
    initialSelectionEnd = selectionEnd;

    if (dragMode === "create") {
      // Start new selection
      selectionStart = dragStartIndex;
      selectionEnd = dragStartIndex;
    }
    // For other modes, keep existing selection and modify during drag
    event.preventDefault();
  }
};

const handleTouchMove = (event: TouchEvent) => {
  if (!isDragging || event.touches.length !== 1) return;

  const touch = event.touches[0];
  const currentIndex = getDataIndexFromEvent(touch);
  if (currentIndex >= 0) {
    switch (dragMode) {
      case "create": {
        // Creating new selection
        selectionEnd = currentIndex;
        break;
      }

      case "resize-left": {
        // Resizing left edge - use initial end
        selectionStart = currentIndex;
        selectionEnd = initialSelectionEnd;
        break;
      }

      case "resize-right": {
        // Resizing right edge - use initial start
        selectionStart = initialSelectionStart;
        selectionEnd = currentIndex;
        break;
      }

      case "move": {
        // Moving entire selection - use initial boundaries
        const dragDistance = currentIndex - dragStartIndex;
        const initialStart = Math.min(
          initialSelectionStart,
          initialSelectionEnd
        );
        const initialEnd = Math.max(initialSelectionStart, initialSelectionEnd);

        const newStart = initialStart + dragDistance;
        const newEnd = initialEnd + dragDistance;

        // Constrain to bounds
        if (newStart >= 0 && newEnd < props.labels.length) {
          selectionStart = newStart;
          selectionEnd = newEnd;
        } else if (newStart < 0) {
          // Hit left boundary
          selectionStart = 0;
          selectionEnd = initialEnd - initialStart;
        } else if (newEnd >= props.labels.length) {
          // Hit right boundary
          selectionEnd = props.labels.length - 1;
          selectionStart = selectionEnd - (initialEnd - initialStart);
        }
        break;
      }
    }

    // Ensure start is before end (except for move mode)
    if (dragMode !== "move" && selectionStart > selectionEnd) {
      [selectionStart, selectionEnd] = [selectionEnd, selectionStart];
    }

    // Redraw chart
    if (chartInstance) {
      chartInstance.update("none");
    }
  }
  event.preventDefault();
};

const handleTouchEnd = () => {
  if (isDragging) {
    isDragging = false;
    dragMode = "none";

    // Ensure proper order
    const actualStart = Math.min(selectionStart, selectionEnd);
    const actualEnd = Math.max(selectionStart, selectionEnd);

    props.onRangeChange(actualStart, actualEnd);
  }
};

// Watch for data changes
watch(
  () => [props.datasets, props.labels],
  (newVal, oldVal) => {
    // Only recreate if data actually changed
    if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
      createRangeChart();

      // Force selection draw after data update
      setTimeout(() => {
        forceSelectionDraw();
      }, 100);
    }
  },
  { deep: true }
);

// Force selection redraw
const forceSelectionDraw = () => {
  if (chartInstance && selectionStart >= 0 && selectionEnd >= 0) {
    chartInstance.update("none");
    chartInstance.render();
  }
};

onMounted(() => {
  createRangeChart();

  // Ensure initial selection is drawn after mount
  requestAnimationFrame(() => {
    setTimeout(() => {
      forceSelectionDraw();
    }, 200);
  });
});

onUnmounted(() => {
  if (chartInstance) {
    chartInstance.destroy();
  }

  // Remove event listeners using helper function
  removeRangeCanvasListeners();
});
</script>

<style scoped>
.range-selector-container {
  width: 100%;
  height: 100px;
  margin-top: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 10px;
  background: #fafafa;
}

.range-selector-container canvas {
  width: 100% !important;
  height: 80px !important;
}
</style>
